import { Box, Container, Flex, Text, Button, Loader, Center, Title, Modal, Paper, ThemeIcon } from "@mantine/core";
import { IconAlertCircle, IconCircleCheck, IconClock, IconDownload } from "@tabler/icons-react";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import AnonymousProfile from "@/components/shared/AnonymousCompanyProfile/AnonymousProfile";
import MatchedProfileView from "@/pages/profile/MatchedView";
import VerificationSubmittedModal from "@/pages/profile-verification/VerificationSubmittedModal";
import RejectionDetailsModal from "@/pages/profile-verification/VerificationRejectedModal";
import VerificationRejectedBanner from "../profile-verification/VerificationRejectedBanner";
import { useUserContext } from "@/context/UserContext";
import { downloadSystemVerificationPdf } from "@/utils/systemVerificationPdf";

const LAST_VERIFICATION_STORAGE_KEY = "my-profile:last-system-verification";
const PROFILE_VERIFICATION_LANDING_KEY = "gt:profileVerificationSummary";

type LandingVerificationKind =
  | "SYSTEM_VERIFIED"
  | "FAILED"
  | "PENDING_ADMIN"
  | "EDIT_LOCKED_UNDER_REVIEW";

export default function CompanyProfileView() {
  const navigate = useNavigate();
  const { userDetails, hasProfile } = useUserContext();
  const [activeView, setActiveView] = useState<"anonymized" | "matched">("anonymized");
  const [company, setCompany] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [verificationSubmittedModalOpened, setVerificationSubmittedModalOpened] = useState(false);
  const [rejectionModalOpened, setRejectionModalOpened] = useState(false);
  const [landingVerificationKind, setLandingVerificationKind] = useState<LandingVerificationKind | null>(null);
  const [verificationData, setVerificationData] = useState<any>(null);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const downloadableVerificationData =
    verificationData ??
    (company?.externalVerificationLogs?.[0]?.metadata as Record<string, unknown> | null) ??
    null;

  const fetchCompanyData = useCallback(async () => {
    if (hasProfile === null) return;
    const companyId = userDetails?.company?.id;

    if (!companyId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const companyData = await trpc.company.getById.query({ companyId });
      setCompany(companyData);
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setLoading(false);
    }
  }, [hasProfile, userDetails?.company?.id]);

  useEffect(() => {
    fetchCompanyData();
  }, [fetchCompanyData]);

  useEffect(() => {
    if (loading || !company?.id) return;
    const raw = sessionStorage.getItem(PROFILE_VERIFICATION_LANDING_KEY);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as { kind?: string };
      sessionStorage.removeItem(PROFILE_VERIFICATION_LANDING_KEY);
      if (
        parsed.kind === "SYSTEM_VERIFIED" ||
        parsed.kind === "FAILED" ||
        parsed.kind === "PENDING_ADMIN" ||
        parsed.kind === "EDIT_LOCKED_UNDER_REVIEW"
      ) {
        setLandingVerificationKind(parsed.kind);
      }
    } catch {
      sessionStorage.removeItem(PROFILE_VERIFICATION_LANDING_KEY);
    }
  }, [loading, company?.id]);

  useEffect(() => {
    const companyId = userDetails?.company?.id;
    if (!companyId || verificationData) return;

    const storedVerificationData = localStorage.getItem(LAST_VERIFICATION_STORAGE_KEY);
    if (!storedVerificationData) return;

    try {
      const parsed = JSON.parse(storedVerificationData) as {
        companyId?: string;
        data?: Record<string, unknown>;
      };

      if (parsed.companyId === companyId && parsed.data) {
        setVerificationData(parsed.data);
      }
    } catch {
      localStorage.removeItem(LAST_VERIFICATION_STORAGE_KEY);
    }
  }, [userDetails?.company?.id, verificationData]);

  const downloadVerificationPdf = async () => {
    if (!downloadableVerificationData) return;
    setPdfDownloading(true);
    try {
      await downloadSystemVerificationPdf(downloadableVerificationData);
    } finally {
      setPdfDownloading(false);
    }
  };

  if (loading) {
    return (
      <Center py="xl" mih="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!company) {
    return (
      <Container size="xl" py="xl">
        <Text>Company not found. Please create your company profile first.</Text>
        <Button mt="md" onClick={() => navigate("/create-profile")}>
          Go to Dashboard
        </Button>
      </Container>
    );
  }

  const companyStatus = company.status;
  const isRejected = companyStatus === "ACTION_REQUIRED";

  const renderHeader = () => (
    <>
      <Box py="md" style={{ borderBottom: "1px solid #E5E7EB" }}>
        <Flex
          justify="space-between"
          pl={20}
          pr={20}
          align="flex-start"
          direction={{ base: "column", sm: "row" }}
          gap="sm"
        >
          <Title order={2} fw={400} fz={24}>
              Welcome! {company.legalName}
          </Title>
          <Flex
            gap="sm"
            align={{ base: "stretch", sm: "center" }}
            direction={{ base: "column", sm: "row" }}
            w={{ base: "100%", sm: "auto" }}
          >
            {downloadableVerificationData && (
              <Button
                variant="light"
                size="sm"
                fz="sm"
                leftSection={<IconDownload size={18} />}
                onClick={() => void downloadVerificationPdf()}
                loading={pdfDownloading}
                disabled={pdfDownloading}
                style={{ whiteSpace: "nowrap" }}
                w={{ base: "100%", sm: "auto" }}
              >
                Download Verification PDF
              </Button>
            )}
          </Flex>
        </Flex>
      </Box>

      <Box py="md">
        <Container size="fluid" px={{ base: "sm", sm: "md" }}>
          <Flex direction="column" gap="sm" w="100%" maw="100%">
            <Flex justify="space-between" align="stretch" wrap="wrap" gap="sm" w="100%">
              <Flex
                direction="column"
                gap="xs"
                w="100%"
                maw={{ base: "100%", md: 1140 }}
                style={{ backgroundColor: "#F3F4F6", padding: "8px", borderRadius: "10px" }}
              >
                <Flex
                  gap="xs"
                  align="stretch"
                  direction={{ base: "column", md: "row" }}
                  wrap="nowrap"
                  w="100%"
                >
                  <Button
                    variant={activeView === "anonymized" ? "filled" : "light"}
                    color="gray"
                    size="md"
                    flex={1}
                    miw={0}
                    w={{ base: "100%", md: "auto" }}
                    maw={{ md: 560 }}
                    onClick={() => setActiveView("anonymized")}
                    styles={{
                      root: {
                        minHeight: "74px",
                        height: "auto",
                        paddingTop: "10px",
                        paddingBottom: "10px",
                        paddingLeft: "12px",
                        paddingRight: "12px",
                        textAlign: "center",
                        justifyContent: "center",
                        backgroundColor:
                          activeView === "anonymized" ? "var(--mantine-color-primary-6)" : "#FFFFFF",
                        color: activeView === "anonymized" ? "#FFFFFF" : "#1F2937",
                        fontWeight: 500,
                        border: "none",
                        boxShadow:
                          activeView === "anonymized"
                            ? "0px 1px 3px rgba(37, 99, 235, 0.35), 0px 1px 2px rgba(37, 99, 235, 0.25)"
                            : "none",
                        "&:hover": {
                          backgroundColor:
                            activeView === "anonymized" ? "var(--mantine-color-primary-7)" : "#F9FAFB",
                        },
                      },
                      inner: {
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                      },
                      label: {
                        width: "100%",
                        textAlign: "center",
                        whiteSpace: "normal",
                      },
                    }}
                  >
                    <Flex direction="column" gap={2} align="center" justify="center" w="100%" maw="100%" px={4}>
                      <Text fw={500} size="sm" c={activeView === "anonymized" ? "#FFFFFF" : "#1F2937"} ta="center" style={{ width: "100%" }}>
                        Anonymized View
                      </Text>
                      <Text size="xs" c={activeView === "anonymized" ? "rgba(255,255,255,0.92)" : "#6B7280"} ta="center" style={{ width: "100%" }}>
                        How your company profile appears to others before revealing your identity.
                      </Text>
                    </Flex>
                  </Button>
                  <Button
                    variant={activeView === "matched" ? "filled" : "light"}
                    color="gray"
                    size="md"
                    flex={1}
                    miw={0}
                    w={{ base: "100%", md: "auto" }}
                    maw={{ md: 560 }}
                    onClick={() => setActiveView("matched")}
                    styles={{
                      root: {
                        minHeight: "74px",
                        height: "auto",
                        paddingTop: "10px",
                        paddingBottom: "10px",
                        paddingLeft: "12px",
                        paddingRight: "12px",
                        textAlign: "center",
                        justifyContent: "center",
                        backgroundColor:
                          activeView === "matched" ? "var(--mantine-color-primary-6)" : "#FFFFFF",
                        color: activeView === "matched" ? "#FFFFFF" : "#1F2937",
                        fontWeight: 500,
                        border: "none",
                        boxShadow:
                          activeView === "matched"
                            ? "0px 1px 3px rgba(37, 99, 235, 0.35), 0px 1px 2px rgba(37, 99, 235, 0.25)"
                            : "none",
                        "&:hover": {
                          backgroundColor:
                            activeView === "matched" ? "var(--mantine-color-primary-7)" : "#F9FAFB",
                        },
                      },
                      inner: {
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                      },
                      label: {
                        width: "100%",
                        textAlign: "center",
                        whiteSpace: "normal",
                      },
                    }}
                  >
                    <Flex direction="column" gap={2} align="center" justify="center" w="100%" maw="100%" px={4}>
                      <Text fw={500} size="sm" c={activeView === "matched" ? "#FFFFFF" : "#1F2937"} ta="center" style={{ width: "100%" }}>
                        Matched View
                      </Text>
                      <Text size="xs" c={activeView === "matched" ? "rgba(255,255,255,0.92)" : "#6B7280"} ta="center" style={{ width: "100%" }}>
                        How your full company profile is shared after a mutual match is established.
                      </Text>
                    </Flex>
                  </Button>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Container>
      </Box>

      {isRejected && (
        <Container size="fluid" mb="md">
          <VerificationRejectedBanner onKnowMore={() => setRejectionModalOpened(true)} />
        </Container>
      )}
    </>
  );

  return (
    <Box w="100%" maw="100%" style={{ minWidth: 0 }}>
      {renderHeader()}

      {activeView === "anonymized" ? (
        <AnonymousProfile pageID="MY_PROFILE_PREVIEW" companyData={company} />
      ) : (
        <MatchedProfileView company={company} containerSize="fluid" isOwnProfile={true} />
      )}

      <VerificationSubmittedModal
        opened={verificationSubmittedModalOpened}
        onClose={() => setVerificationSubmittedModalOpened(false)}
      />

      <RejectionDetailsModal
        opened={rejectionModalOpened}
        onClose={() => setRejectionModalOpened(false)}
        adminComments={company?.adminComments}
      />

      <Modal
        opened={landingVerificationKind !== null}
        onClose={() => setLandingVerificationKind(null)}
        title={
          landingVerificationKind === "SYSTEM_VERIFIED"
            ? "Registry verification succeeded"
            : landingVerificationKind === "FAILED"
              ? "Registry verification did not succeed"
              : landingVerificationKind === "EDIT_LOCKED_UNDER_REVIEW"
                ? "Edit profile is temporarily locked"
                : "Verification pending review"
        }
        centered
        size="md"
      >
        {landingVerificationKind === "SYSTEM_VERIFIED" && (
          <Box>
            <Paper p="md" radius="md" mb="md" bg="green.0" style={{ border: "1px solid var(--mantine-color-green-2)" }}>
              <Flex gap="sm" align="center">
                <ThemeIcon size={28} radius="xl" color="green" variant="light">
                  <IconCircleCheck size={18} />
                </ThemeIcon>
                <Text size="sm" c="green.8" fw={600}>
                  Your company matched the official registry (e.g. GST / other ID). Your profile is system-verified.
                </Text>
              </Flex>
            </Paper>
            <Text size="sm" c="dimmed" style={{ lineHeight: 1.6 }}>
              Final platform verification may still require an administrator for discovery listing. To change registered
              details and <strong>run registry verification again</strong>, go to <strong>Profile → Edit profile</strong>
              , update the form, and click <strong>Save</strong>.
            </Text>
            <Flex justify="flex-end" mt="lg">
              <Button onClick={() => setLandingVerificationKind(null)}>OK</Button>
            </Flex>
          </Box>
        )}
        {landingVerificationKind === "FAILED" && (
          <Box>
            <Paper p="md" radius="md" mb="md" bg="red.0" style={{ border: "1px solid var(--mantine-color-red-2)" }}>
              <Flex gap="sm" align="center">
                <ThemeIcon size={28} radius="xl" color="red" variant="light">
                  <IconAlertCircle size={18} />
                </ThemeIcon>
                <Text size="sm" c="red.8" fw={600}>
                  The registry did not confirm your company with the details provided (check GSTIN / legal name /
                  state for India).
                </Text>
              </Flex>
            </Paper>
            <Text size="sm" c="dimmed" style={{ lineHeight: 1.6 }}>
              Open <strong>Edit profile</strong>, correct your registration information, save, and complete the
              verification steps again.
            </Text>
            <Flex justify="flex-end" gap="sm" mt="lg" wrap="wrap">
              <Button variant="default" onClick={() => setLandingVerificationKind(null)}>
                Close
              </Button>
              <Button onClick={() => navigate("/profile/edit")}>Edit profile</Button>
            </Flex>
          </Box>
        )}
        {landingVerificationKind === "PENDING_ADMIN" && (
          <Box>
            <Paper p="md" radius="md" mb="md" bg="blue.0" style={{ border: "1px solid var(--mantine-color-blue-2)" }}>
              <Flex gap="sm" align="center">
                <ThemeIcon size={28} radius="xl" color="blue" variant="light">
                  <IconClock size={18} />
                </ThemeIcon>
                <Text size="sm" c="blue.8" fw={600}>
                  Your registration is queued for admin registry review (often within 24 hours).
                </Text>
              </Flex>
            </Paper>
            <Text size="sm" c="dimmed" style={{ lineHeight: 1.6 }}>
              You will be notified when your profile has been reviewed. Credits for this path are charged when an
              administrator approves your company.
            </Text>
            <Flex justify="flex-end" mt="lg">
              <Button onClick={() => setLandingVerificationKind(null)}>OK</Button>
            </Flex>
          </Box>
        )}
        {landingVerificationKind === "EDIT_LOCKED_UNDER_REVIEW" && (
          <Box>
            <Paper p="md" radius="md" mb="md" bg="yellow.0" style={{ border: "1px solid var(--mantine-color-yellow-3)" }}>
              <Flex gap="sm" align="center">
                <ThemeIcon size={28} radius="xl" color="yellow" variant="light">
                  <IconClock size={18} />
                </ThemeIcon>
                <Text size="sm" c="yellow.9" fw={600}>
                  Your company is currently in admin verification review. Editing is disabled until verification is completed.
                </Text>
              </Flex>
            </Paper>
            <Text size="sm" c="dimmed" style={{ lineHeight: 1.6 }}>
              Admin can complete verification from the pending verification queue. Once status is updated, profile editing
              will be available again.
            </Text>
            <Flex justify="flex-end" mt="lg">
              <Button onClick={() => setLandingVerificationKind(null)}>OK</Button>
            </Flex>
          </Box>
        )}
      </Modal>
    </Box>
  );
}
